import React, { useState, useEffect } from "react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { addPatient, searchPatients } from "@/lib/api"
import { toast } from "@/hooks/use-toast"
import { cn } from "@/lib/utils"
import { 
  User, 
  Phone, 
  Mail, 
  Calendar, 
  AlertTriangle, 
  UserPlus,
  Loader2,
  Users,
  CheckCircle,
  Stethoscope
} from "lucide-react"
import { useAuth } from "@/context/AuthContext"

interface FormData {
  name: string
  age: string
  gender: string
  phone: string
  email: string
}

interface ExistingPatient {
  id: string
  name: string
  age?: number
  gender?: string
  phone?: string
  email?: string
}

interface DuplicateInfo {
  type: 'name' | 'phone'
  patients: ExistingPatient[]
}

type SubmitIntent = 'patient-only' | 'patient-and-rx'

type Props = {
  onSuccess?: (newPatient: any, intent: SubmitIntent) => void
  onCancel?: () => void
  compact?: boolean
}

export default function AddPatientForm({ onSuccess, onCancel, compact = false }: Props) {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    age: "",
    gender: "Male",
    phone: "",
    email: ""
  })
  const { currentUser } = useAuth();
  
  const [loading, setLoading] = useState(false)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [duplicateInfo, setDuplicateInfo] = useState<DuplicateInfo | null>(null)
  const [userConfirmedDuplicate, setUserConfirmedDuplicate] = useState(false)

  // Real-time duplicate checking
  useEffect(() => {
    const checkForDuplicates = async () => {
      // Only check if we have meaningful data
      if (formData.name.trim().length < 2 && formData.phone.trim().length < 10) {
        setDuplicateInfo(null)
        setUserConfirmedDuplicate(false)
        return
      }

      try {
        let duplicateFound: DuplicateInfo | null = null
        
        // Check name
        if (formData.name.trim().length >= 2) {
          const nameResults = await searchPatients(formData.name.trim(), currentUser?.clinic_id)
          const nameArray = Array.isArray(nameResults) ? nameResults : []
          const nameMatches = nameArray.filter(patient => 
            patient.name?.toLowerCase() === formData.name.toLowerCase()
          )
          if (nameMatches.length > 0) {
            duplicateFound = { type: 'name', patients: nameMatches }
          }
        }
        
        // Check phone
        if (!duplicateFound && formData.phone.trim().length === 10) {
          const phoneResults = await searchPatients(formData.phone.trim(), currentUser?.clinic_id)
          const phoneArray = Array.isArray(phoneResults) ? phoneResults : []
          const phoneMatches = phoneArray.filter(patient => patient.phone === formData.phone)
          if (phoneMatches.length > 0) {
            duplicateFound = { type: 'phone', patients: phoneMatches }
          }
        }

        setDuplicateInfo(duplicateFound)
        // Reset confirmation when duplicate info changes
        setUserConfirmedDuplicate(false)
        
      } catch (error) {
        console.error('Error checking duplicates:', error)
        setDuplicateInfo(null)
        setUserConfirmedDuplicate(false)
      }
    }

    // Debounce the duplicate check
    const timeoutId = setTimeout(checkForDuplicates, 800)
    return () => clearTimeout(timeoutId)
  }, [formData.name, formData.phone])

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    // Required fields
    if (!formData.name.trim()) {
      errors.name = "Patient name is required"
    } else if (formData.name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters"
    }

    // Age validation
    if (formData.age) {
      const ageNum = Number.parseInt(formData.age)
      if (Number.isNaN(ageNum) || ageNum < 0 || ageNum > 120) {
        errors.age = "Please enter a valid age (0-120)"
      }
    }

    // Phone validation
    if (formData.phone && !/^\d{10}$/.test(formData.phone)) {
      errors.phone = "Phone number must be exactly 10 digits"
    }

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address"
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (intent: SubmitIntent) => {
    if (!validateForm()) {
      toast({ 
        title: "Please fix the validation errors", 
        variant: "destructive" 
      })
      return
    }

    // If duplicates were found but user hasn't confirmed, ask for confirmation
    if (duplicateInfo && !userConfirmedDuplicate) {
      toast({
        title: "Please confirm",
        description: "Review the existing patient(s) below and click 'This is a different patient' if this is someone else.",
        variant: "default"
      })
      return
    }

    setLoading(true)

    try {
      const patientData = {
        name: formData.name.trim(),
        age: formData.age ? parseInt(formData.age) : 0,
        gender: formData.gender,
        phone: formData.phone.trim() || "",
        email: formData.email.trim() || "",
        doctor_id: currentUser?.doctor_id,
        clinic_id: currentUser?.clinic_id
      }

      const apiResponse = await addPatient(patientData)

      // Check if the API call was successful
      if (!apiResponse?.id) {
        throw new Error("Failed to create patient - no ID returned from server")
      }

      // Construct the full patient object with the returned ID
      const newPatient = {
        id: apiResponse.id,
        ...patientData,
        // Add timestamps for consistency
        created_at: new Date(),
        updated_at: new Date()
      }

      const intentText = intent === 'patient-and-rx' ? 'and prescription ready' : 'successfully'
      toast({ 
        title: "Patient Added Successfully", 
        description: `${formData.name} has been registered ${intentText}.`
      })
      
      onSuccess?.(newPatient, intent)

      // Reset form
      setFormData({
        name: "",
        age: "",
        gender: "Male",
        phone: "",
        email: ""
      })
      setValidationErrors({})
      setDuplicateInfo(null)
      setUserConfirmedDuplicate(false)
      
    } catch (error) {
      console.error("Failed to add patient:", error)
      toast({ 
        title: "Failed to add patient", 
        description: "Please try again.",
        variant: "destructive" 
      })
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmDuplicate = () => {
    setUserConfirmedDuplicate(true)
    toast({
      title: "Confirmed",
      description: "You can now proceed to add this as a new patient.",
      variant: "default"
    })
  }

  const updateFormData = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: "" }))
    }
    // Reset confirmation when user changes name or phone
    if ((field === 'name' || field === 'phone') && userConfirmedDuplicate) {
      setUserConfirmedDuplicate(false)
    }
  }

  const canSubmit = !loading && formData.name.trim() && (!duplicateInfo || userConfirmedDuplicate)

  return (
    <div className={cn("space-y-6", compact && "space-y-4")}>
      {/* Real-time Duplicate Information - Shows existing patient details */}
      {duplicateInfo && (
        <Alert className={cn(
          "border-warning/30 bg-warning/5 dark:bg-warning/10",
          userConfirmedDuplicate && "border-success/30 bg-success/5 dark:bg-success/10",
          compact && "p-3"
        )}>
          <Users className={cn(
            "h-4 w-4",
            userConfirmedDuplicate ? "text-success" : "text-warning"
          )} />
          <AlertDescription className={cn(
            "text-foreground"
          )}>
            <div className={cn("space-y-3", compact && "space-y-2")}>
              <div className="font-medium flex items-center gap-2">
                {userConfirmedDuplicate ? (
                  <>
                    <CheckCircle className="h-4 w-4 text-success" />
                    <span className="text-success">Confirmed: Adding as new patient</span>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-4 w-4 text-warning" />
                    <span className="text-warning">
                    {duplicateInfo.type === 'name' ? 'Similar name found' : 'Phone number already exists'}
                    </span>
                  </>
                )}
              </div>
              {!userConfirmedDuplicate && (
                <div className={cn(
                  "text-sm",
                  compact && "space-y-1"
                )}>
                  <p className={cn(
                    "mb-2 text-foreground",
                    compact && "mb-1 text-xs"
                  )}>
                    Is this the same patient? Review existing record{duplicateInfo.patients.length > 1 ? 's' : ''}:
                  </p>
                  <div className={cn(
                    "space-y-2",
                    compact && "space-y-1"
                  )}>
                    {duplicateInfo.patients.map((patient, index) => (
                      <div key={patient.id || index} className={cn(
                        "bg-card dark:bg-card/80 rounded-md border border-warning/20",
                        compact ? "p-2" : "p-3"
                      )}>
                        <div className={cn(
                          "grid gap-2 text-xs",
                          compact ? "grid-cols-2 lg:grid-cols-4" : "grid-cols-2 md:grid-cols-4"
                        )}>
                          <div>
                            <span className="font-medium text-muted-foreground">Name:</span>
                            <div className="text-foreground truncate">{patient.name}</div>
                          </div>
                          <div>
                            <span className="font-medium text-muted-foreground">Age:</span>
                            <div className="text-foreground">{patient.age || 'N/A'}</div>
                          </div>
                          <div>
                            <span className="font-medium text-muted-foreground">Gender:</span>
                            <div className="text-foreground">{patient.gender || 'N/A'}</div>
                          </div>
                          <div>
                            <span className="font-medium text-muted-foreground">Phone:</span>
                            <div className="text-foreground">{patient.phone || 'N/A'}</div>
                          </div>
                        </div>
                        {patient.email && !compact && (
                          <div className="mt-2 text-xs">
                            <span className="font-medium text-muted-foreground">Email:</span>
                            <span className="ml-2 text-foreground">{patient.email}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleConfirmDuplicate}
                      className="text-warning border-warning/30 hover:bg-warning/10"
                    >
                      <CheckCircle className="w-3 h-3 mr-1" />
                      This is a different patient
                    </Button>
                  </div>
                  <p className="mt-2 text-xs text-foreground">
                    💡 If this is a different patient, click "This is a different patient" above, or consider using middle name, nickname, or different phone number.
                  </p>
                </div>
              )}
              {userConfirmedDuplicate && (
                <p className="text-sm text-success">
                  ✅ Ready to add as a new patient. Choose your action below.
                </p>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Form Fields */}
      <div className={cn(
        "grid grid-cols-1 md:grid-cols-2 gap-4",
        compact && "gap-3"
      )}>
        <div className={cn("space-y-2", compact && "space-y-1")}>
          <Label htmlFor="name" className={cn(
            "text-sm font-medium flex items-center gap-2",
            compact && "text-xs"
          )}>
            <User className="w-4 h-4 text-primary" />
            Patient Name *
          </Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => updateFormData('name', e.target.value)}
            placeholder="Enter patient full name"
            className={cn(
              compact ? "h-9" : "h-11",
              validationErrors.name && "border-destructive focus:border-destructive",
              duplicateInfo?.type === 'name' && !userConfirmedDuplicate && "border-warning focus:border-warning",
              duplicateInfo?.type === 'name' && userConfirmedDuplicate && "border-success focus:border-success"
            )}
            required
          />
          {validationErrors.name && (
            <p className={cn("text-sm text-destructive", compact && "text-xs")}>{validationErrors.name}</p>
          )}
        </div>
        
        <div className={cn("space-y-2", compact && "space-y-1")}>
          <Label htmlFor="age" className={cn(
            "text-sm font-medium flex items-center gap-2",
            compact && "text-xs"
          )}>
            <Calendar className="w-4 h-4 text-primary" />
            Age
          </Label>
          <Input
            id="age"
            type="number"
            value={formData.age}
            onChange={(e) => updateFormData('age', e.target.value)}
            placeholder="Enter age"
            className={cn(
              compact ? "h-9" : "h-11",
              validationErrors.age && "border-destructive focus:border-destructive"
            )}
            min="0"
            max="120"
          />
          {validationErrors.age && (
            <p className={cn("text-sm text-destructive", compact && "text-xs")}>{validationErrors.age}</p>
          )}
        </div>
        
        <div className={cn("space-y-2", compact && "space-y-1")}>
          <Label htmlFor="gender" className={cn(
            "text-sm font-medium",
            compact && "text-xs"
          )}>
            Gender
          </Label>
          <Select value={formData.gender} onValueChange={(value) => updateFormData('gender', value)}>
            <SelectTrigger id="gender" className={compact ? "h-9" : "h-11"}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Male">Male</SelectItem>
              <SelectItem value="Female">Female</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className={cn("space-y-2", compact && "space-y-1")}>
          <Label htmlFor="phone" className={cn(
            "text-sm font-medium flex items-center gap-2",
            compact && "text-xs"
          )}>
            <Phone className="w-4 h-4 text-primary" />
            Phone Number *
          </Label>
          <Input
            id="phone"
            value={formData.phone}
            onChange={(e) => updateFormData('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
            placeholder="Enter 10-digit phone number"
            className={cn(
              compact ? "h-9" : "h-11",
              validationErrors.phone && "border-destructive focus:border-destructive",
              duplicateInfo?.type === 'phone' && !userConfirmedDuplicate && "border-warning focus:border-warning",
              duplicateInfo?.type === 'phone' && userConfirmedDuplicate && "border-success focus:border-success"
            )}
            maxLength={10}
            required
          />
          {validationErrors.phone && (
            <p className={cn("text-sm text-destructive", compact && "text-xs")}>{validationErrors.phone}</p>
          )}
        </div>
        
        <div className={cn("space-y-2 md:col-span-2", compact && "space-y-1")}>
          <Label htmlFor="email" className={cn(
            "text-sm font-medium flex items-center gap-2",
            compact && "text-xs"
          )}>
            <Mail className="w-4 h-4 text-primary" />
            Email Address
          </Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => updateFormData('email', e.target.value)}
            placeholder="Enter email address"
            className={cn(
              compact ? "h-9" : "h-11",
              validationErrors.email && "border-destructive focus:border-destructive"
            )}
          />
          {validationErrors.email && (
            <p className={cn("text-sm text-destructive", compact && "text-xs")}>{validationErrors.email}</p>
          )}
        </div>
      </div>

      {/* Action Buttons - Two Submit Options */}
      <div className={cn(
        "flex gap-3 justify-end pt-4 border-t border-border",
        compact && "pt-3 gap-2"
      )}>
        {onCancel && (
          <Button 
            type="button" 
            variant="outline" 
            onClick={onCancel} 
            disabled={loading}
            className={cn(
              "px-6 medical-button-secondary",
              compact && "px-4 text-sm h-9"
            )}
          >
            Cancel
          </Button>
        )}
        
        <Button 
          onClick={() => handleSubmit('patient-only')}
          disabled={!canSubmit}
          variant="outline"
          className={cn(
            "px-6 medical-button-primary",
            compact && "px-4 text-sm h-9"
          )}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <UserPlus className="w-4 h-4 mr-2" />
          )}
          Create Patient
        </Button>
        
        <Button 
          onClick={() => handleSubmit('patient-and-rx')}
          disabled={!canSubmit}
          className={cn(
            "px-6 medical-button-primary",
            "bg-medical-schedule hover:bg-medical-schedule/90 text-white",
            "border-medical-schedule hover:border-medical-schedule/90",
            duplicateInfo && userConfirmedDuplicate && "bg-medical-schedule hover:bg-medical-schedule/90",
            compact && "px-4 text-sm h-9"
          )}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Stethoscope className="w-4 h-4 mr-2" />
          )}
          Create Patient and Rx
        </Button>
      </div>
    </div>
  )
}
